/**
 * JD-Based Analysis Service
 * Dynamically extracts competencies from JD and maps exam questions accordingly
 * No static classification - everything is JD-driven
 */
import { getApiBaseUrl } from './ApiUtils';
import firebase from '../config';

/**
 * Extract competencies from Job Description using AI
 * @param {string} jdText - Job description text
 * @returns {Promise<Array>} Array of competency objects
 */
const extractCompetenciesFromJD = async (jdText) => {
  try {
    const apiUrl = getApiBaseUrl();
    
    const response = await fetch(`${apiUrl}/api/extract-jd-competencies`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jdText: jdText.substring(0, 5000) // Limit to 5000 chars for API
      })
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success) {
      return result.competencies || [];
    } else {
      throw new Error(result.error || 'Failed to extract competencies');
    }

  } catch (error) {
    console.error('JD competency extraction error:', error);
    throw error;
  }
};

/**
 * Map exam questions to JD-based competencies
 * @param {Object} examData - Exam structure with questions
 * @param {Array} jdCompetencies - Competencies extracted from JD
 * @returns {Promise<Object>} Mapping of question IDs to competencies
 */
const mapQuestionsToJDCompetencies = async (examData, jdCompetencies) => {
  try {
    if (!examData || !examData.sections || !jdCompetencies || jdCompetencies.length === 0) {
      return {};
    }

    // Collect all questions with their text
    const questions = [];
    examData.sections.forEach((section, sectionIndex) => {
      const sectionQuestions = section.q || section.questions || [];
      sectionQuestions.forEach((question, questionIndex) => {
        questions.push({
          id: question.id || `q_${sectionIndex}_${questionIndex}`,
          text: question.q || question.text || question.question || '',
          sectionTitle: section.t || section.title || '',
          sectionIndex,
          questionIndex
        });
      });
    });

    if (questions.length === 0) {
      return {};
    }

    // Limit questions to avoid token limits (process in batches if needed)
    const maxQuestionsPerBatch = 50;
    const questionBatches = [];
    for (let i = 0; i < questions.length; i += maxQuestionsPerBatch) {
      questionBatches.push(questions.slice(i, i + maxQuestionsPerBatch));
    }

    const allMappings = {};
    const apiUrl = getApiBaseUrl();
    
    // Process each batch
    for (const batch of questionBatches) {
      try {
        const response = await fetch(`${apiUrl}/api/map-questions-to-competencies`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            questions: batch.map(q => ({
              id: q.id,
              text: q.text.substring(0, 500), // Limit question text
              sectionTitle: q.sectionTitle
            })),
            competencies: jdCompetencies
          })
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.success && result.mapping) {
          Object.assign(allMappings, result.mapping);
        }
      } catch (batchError) {
        console.error(`Error mapping question batch:`, batchError);
        // Continue with other batches
      }
    }

    return allMappings;

  } catch (error) {
    console.error('Question mapping error:', error);
    // Fallback: return empty mapping
    return {};
  }
};

/**
 * Fetch assessment data and map to JD competencies
 * @param {string} email - Candidate email
 * @param {Array<string>} examCodes - Exam codes
 * @param {string} jdText - Job description text
 * @returns {Promise<Object>} Assessment data mapped to JD competencies
 */
const fetchAndMapAssessmentData = async (email, examCodes, jdText) => {
  try {
    // Step 1: Extract competencies from JD
    const jdCompetencies = await extractCompetenciesFromJD(jdText);
    
    if (!jdCompetencies || jdCompetencies.length === 0) {
      throw new Error('Failed to extract competencies from Job Description');
    }

    const assessmentData = {
      candidateEmail: email,
      examCodes: examCodes,
      jdCompetencies: jdCompetencies,
      competencyResults: {}, // Will be keyed by JD competency names
      examDates: [],
      candidateName: '',
      questionMappings: {} // Store question to competency mappings
    };

    // Step 2: Process each exam
    for (const examCode of examCodes) {
      try {
        // Fetch exam structure
        const examRef = firebase.database().ref(`TestPapers/${examCode}`);
        const examSnapshot = await examRef.once('value');
        
        if (!examSnapshot.exists()) {
          continue;
        }

        const examData = examSnapshot.val();

        // Step 3: Map questions to JD competencies
        const questionMapping = await mapQuestionsToJDCompetencies(examData, jdCompetencies);
        assessmentData.questionMappings[examCode] = questionMapping;

        // Step 4: Fetch exam results
        const resultRef = firebase.database().ref(`Exam_results/${examCode}/${email}`);
        const resultSnapshot = await resultRef.once('value');
        
        if (!resultSnapshot.exists()) {
          continue;
        }

        const resultData = resultSnapshot.val();

        // Store candidate name from first exam
        if (!assessmentData.candidateName && resultData.name) {
          assessmentData.candidateName = resultData.name;
        }

        // Store exam date
        if (resultData.submittedAt) {
          const examDate = new Date(resultData.submittedAt).toLocaleString();
          assessmentData.examDates.push(`${examCode}: ${examDate}`);
        }

        // Step 5: Process evaluations and map to JD competencies
        if (resultData.aiEvaluation && resultData.aiEvaluation.evaluations) {
          const evaluations = resultData.aiEvaluation.evaluations;

          // Initialize competency results
          jdCompetencies.forEach(comp => {
            if (!assessmentData.competencyResults[comp.name]) {
              assessmentData.competencyResults[comp.name] = {
                correct: 0,
                total: 0,
                maxScore: 0,
                description: comp.description || '',
                importance: comp.importance || 'medium'
              };
            }
          });

          // Process each evaluation
          for (const [questionId, evaluation] of Object.entries(evaluations)) {
            const score = evaluation.score || 0;
            const maxScore = evaluation.maxScore || 1;

            // Find which JD competency this question maps to
            const mappedCompetency = questionMapping[questionId];
            
            if (mappedCompetency && assessmentData.competencyResults[mappedCompetency]) {
              // Count correct answers (score > 0)
              if (score > 0) {
                assessmentData.competencyResults[mappedCompetency].correct += 1;
              }
              assessmentData.competencyResults[mappedCompetency].total += 1;
              assessmentData.competencyResults[mappedCompetency].maxScore += maxScore;
            }
          }
        }

      } catch (examError) {
        console.error(`Error processing exam ${examCode}:`, examError);
        continue;
      }
    }

    // Filter out competencies with no questions
    Object.keys(assessmentData.competencyResults).forEach(comp => {
      if (assessmentData.competencyResults[comp].total === 0) {
        delete assessmentData.competencyResults[comp];
      }
    });

    if (Object.keys(assessmentData.competencyResults).length === 0) {
      throw new Error('No assessment data found for the provided exam codes');
    }

    assessmentData.examDates = assessmentData.examDates.join('; ');

    return assessmentData;

  } catch (error) {
    console.error('Error fetching and mapping assessment data:', error);
    throw error;
  }
};

/**
 * Process assessment data with JD-based competencies
 * @param {Object} assessmentData - Assessment data with JD competencies
 * @param {string} email - Candidate email
 * @param {string} role - Role
 * @param {Array<string>} examCodes - Exam codes
 * @returns {Object} Processed analysis
 */
const processJDBasedAssessment = (assessmentData, email, role, examCodes) => {
  // Calculate scores for each JD competency
  const competencyScores = Object.entries(assessmentData.competencyResults).map(([competencyName, result]) => {
    const percentage = result.total > 0 ? Math.round((result.correct / result.total) * 100) : 0;
    
    // Readiness level mapping
    let readinessLevel = 1;
    if (percentage >= 90) readinessLevel = 5;
    else if (percentage >= 80) readinessLevel = 4;
    else if (percentage >= 70) readinessLevel = 3;
    else if (percentage >= 50) readinessLevel = 2;

    return {
      competency: competencyName,
      description: result.description || '',
      importance: result.importance || 'medium',
      raw: `${result.correct}/${result.total}`,
      percentage,
      readinessLevel,
      maxScore: result.maxScore
    };
  });

  // Sort by importance and then by percentage
  competencyScores.sort((a, b) => {
    const importanceOrder = { 'high': 3, 'medium': 2, 'low': 1 };
    const aImp = importanceOrder[a.importance] || 2;
    const bImp = importanceOrder[b.importance] || 2;
    
    if (aImp !== bImp) {
      return bImp - aImp; // High importance first
    }
    return b.percentage - a.percentage; // Higher percentage first
  });

  return {
    candidateEmail: email,
    candidateName: assessmentData.candidateName || email.split('@')[0],
    role: role,
    examCodes: examCodes,
    totalAssessments: examCodes.length,
    jdCompetencies: assessmentData.jdCompetencies,
    competencyScores: competencyScores,
    examDates: assessmentData.examDates
  };
};

const JDBasedAnalysisService = {
  extractCompetenciesFromJD,
  mapQuestionsToJDCompetencies,
  fetchAndMapAssessmentData,
  processJDBasedAssessment
};

export default JDBasedAnalysisService;

